import { useQuery } from '@tanstack/react-query';
import { StrapiFAQResponse } from '@/types/faq';
import { mapStrapiSeo, ProcessedStrapiSeo } from '@/services/strapi/strapiHomeSeo';

interface UseFAQReturn {
  faqData: StrapiFAQResponse | null;
  seo: ProcessedStrapiSeo | null;
  loading: boolean;
  error: string | null;
  refetch: () => void;
}

const fetchFAQData = async (): Promise<StrapiFAQResponse> => {
  const response = await fetch('/api/strapi/faq');
  
  if (!response.ok) {
    const errorData = await response.json().catch(() => ({ error: 'Error desconocido' }));
    throw new Error(`Error HTTP ${response.status}: ${errorData.error || response.statusText}`);
  }
  
  const data: StrapiFAQResponse = await response.json();
  
  // Validar que los datos tengan la estructura esperada
  if (!data || !data.data) {
    throw new Error('Estructura de datos inválida recibida del servidor');
  }
  
  return data;
};

export const useFAQ = (): UseFAQReturn => {
  const {
    data: faqData,
    isLoading: loading,
    error,
    refetch
  } = useQuery<StrapiFAQResponse, Error>({
    queryKey: ['strapi-faq'],
    queryFn: fetchFAQData,
    staleTime: 5 * 60 * 1000, // 5 minutos
    gcTime: 10 * 60 * 1000, // 10 minutos (gcTime reemplaza a cacheTime en v5)
    retry: 3,
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
  });

  return {
    faqData: faqData || null,
    seo: mapStrapiSeo(faqData?.data?.SEO) || null,
    loading,
    error: error?.message || null,
    refetch: () => refetch()
  };
};

